import { useCallback, useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";

const TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_PHOTOS = 5;
const EMPTY = { service: "", appt_type: "", date: null, time: "", notes: "", agree: false, lux_style: "",
  name: "", email: "", phone: "" };
export const isLux = (service) => service.startsWith("Retwist + Lux");

export function useBooking() {
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const photosRef = useRef(photos);
  photosRef.current = photos;

  useEffect(() => {
    api.get("/booking-options").then((r) => setOptions(r.data)).catch(() => setOptions(null));
  }, []);

  useEffect(() => () => { photosRef.current.forEach((p) => URL.revokeObjectURL(p.url)); }, []);

  const loadSlots = useCallback(async (date) => {
    if (!date) { setSlots([]); return; }
    setSlotsLoading(true);
    try {
      const r = await api.get("/availability", { params: { date: format(date, "yyyy-MM-dd") } });
      const list = Array.isArray(r.data?.slots) ? r.data.slots : [];
      setSlots(list);
      setForm((f) => (list.some((s) => s.time === f.time && s.available) ? f : { ...f, time: "" }));
    } catch {
      setSlots([]);
      toast.error("Couldn't load open times. Please try again.");
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  useEffect(() => { loadSlots(form.date); }, [form.date, loadSlots]);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const addPhotos = (fileList) => {
    const files = Array.from(fileList || []);
    const ok = files.filter((f) => TYPES.includes(f.type) && f.size <= 5 * 1024 * 1024);
    if (ok.length < files.length) toast.error("Photos must be JPG, PNG or WebP and under 5 MB each.");
    const room = MAX_PHOTOS - photos.length;
    if (ok.length > room) toast.error(`You can upload up to ${MAX_PHOTOS} photos.`);
    const next = ok.slice(0, Math.max(room, 0)).map((file) => ({ file, url: URL.createObjectURL(file) }));
    if (next.length) {
      setPhotos((p) => [...p, ...next]);
      setErrors((e) => ({ ...e, photos: undefined }));
    }
  };

  const removePhoto = (i) => {
    setPhotos((p) => { URL.revokeObjectURL(p[i].url); return p.filter((_, j) => j !== i); });
  };

  const startSubmit = () => {
    const e = {};
    if (!form.service) e.service = "Please select your service.";
    if (!form.appt_type) e.appt_type = "Choose In-Studio or Mobile.";
    if (!form.date) e.date = "Pick a date.";
    if (!form.time) e.time = "Select a time.";
    if (!photos.length) e.photos = "Upload at least one clear photo of your hair.";
    if (isLux(form.service) && !form.notes.trim()) e.notes = "Tell me which Lux Style look you want in the notes.";
    if (!form.name.trim()) e.name = "Tell me your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email so I can confirm.";
    if (form.phone.replace(/\D/g, "").length < 7) e.phone = "Enter a phone number so I can text you.";
    if (isLux(form.service) && !form.lux_style) e.lux_style = "Choose your Lux Style.";
    if (!form.agree) e.agree = "Please agree to the House Rules.";
    setErrors(e);
    const first = Object.values(e)[0];
    if (first) { toast.error(first); return; }
    submit();
  };

  const submit = async () => {
    setSubmitting(true);
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("email", form.email);
    fd.append("phone", form.phone);
    fd.append("service", form.service);
    fd.append("appt_type", form.appt_type);
    fd.append("date", format(form.date, "yyyy-MM-dd"));
    fd.append("time", form.time);
    fd.append("notes", form.notes);
    fd.append("agree", "true");
    if (isLux(form.service)) fd.append("lux_style", form.lux_style);
    photos.forEach((p) => fd.append("photos", p.file));
    try {
      const r = await api.post("/bookings", fd);
      setSuccess({ ...r.data, name: form.name, dateLabel: format(form.date, "EEEE, MMMM d"), appt_type: form.appt_type });
      photos.forEach((p) => URL.revokeObjectURL(p.url));
      setPhotos([]);
      setForm(EMPTY);
    } catch (err) {
      toast.error(errorText(err, "Your request couldn't be sent. Please try again."));
      if (err?.response?.status === 409) loadSlots(form.date);
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = options?.min_date ? parseISO(options.min_date) : null;
  const maxDate = options?.max_date ? parseISO(options.max_date) : null;

  return {
    options, form, set, photos, addPhotos, removePhoto, slots, slotsLoading, errors,
    minDate, maxDate, startSubmit, submitting, success, setSuccess,
  };
}
